// src/client/renderer/vfx/items/soul-duel.ts
//
// Plan 17, Task 5.3 — Soul Duel pool (challenge portal + resolution).
//
// Triggered by THREE events:
//
//   1. ItemCastEvent (itemId='soul_duel') → phase='cast':
//        Cast portal at caster position (300ms anticipation).
//        Pool: one-shot, max-active=2.
//
//   2. SoulDuelTriggeredEvent → phase='active':
//        TWO portals (challenger + defender) connected by a duel-link beam.
//        Long-lived: persists until resolution.
//        Single-instance per challengerId+defenderId pair.
//        Pool: Map keyed by `${challengerId}|${defenderId}`, max-active=4 pairs.
//        F6 release-first: on duplicate SoulDuelTriggeredEvent for an already-active
//        pair, release prior duel resources BEFORE acquiring new ones.
//
//   3. SoulDuelResolvedEvent → phase='impact':
//        Portals collapse (active duel released), winner gets a golden flare
//        (500ms scale 0→2.0, opacity 1→0).
//        Pool: resolution flare one-shot max-active=4.
//
// Compound key encoding (casterId overload):
//   `ItemVfxSpawnArgs` has no `defenderId` field. The compound key is encoded:
//   - phase='active': args.casterId = `${challengerId}|${defenderId}`
//     args.casterPosition = challenger anchor; args.targetPosition = defender anchor.
//   - phase='cast': args.casterId = challengerId (single anchor only).
//   - phase='impact': args.casterId = `${challengerId}|${defenderId}` (for pool lookup
//     to release the active duel); args.targetPosition = winner position (flare anchor).
//
// Colours:
//   0xff3366 (crimson) for challenger portal.
//   0x3366ff (blue)    for defender portal.
//   0xffd24a (gold)    for resolution flare.
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Triggered and resolved
//   duel visuals are intentionally silent follow-up emitters.
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry once
//   per frame across all pools.
//
// F4 dispose iterates ALL three active sets/maps:
//   cast portal activeSet + active duel Map<pairKey, DuelState> + resolution flare activeSet.
// F5 acquire-after-dispose returns null (closure `disposed` flag).
// F6 release-first per pair on re-trigger (critical — reviewer note SW1).

import * as THREE from 'three';
import type {
  GameEvent,
  ItemCastEvent,
  SoulDuelTriggeredEvent,
  SoulDuelResolvedEvent,
} from '../../../../shared/game/types';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Cast portal max active slots. */
const MAX_CAST_ACTIVE = 2;

/** Active duel max concurrent pairs. */
const MAX_ACTIVE_DUELS = 4;

/** Resolution flare max active slots. */
const MAX_FLARE_ACTIVE = 4;

/** Cast portal anticipation lifetime (300ms). */
const CAST_LIFETIME = 0.3;

/** Resolution flare lifetime (500ms). */
const FLARE_LIFETIME = 0.5;

/** Challenger portal: crimson. */
const CHALLENGER_COLOUR = 0xff3366;

/** Defender portal: blue. */
const DEFENDER_COLOUR = 0x3366ff;

/** Resolution flare: gold. */
const FLARE_COLOUR = 0xffd24a;

// ─────────────────────────────────────────────────────────────────────────────
// Internal state interfaces
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

interface DuelState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  emitter: THREE.Group;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a single portal swirl Group with a sprite + ring at `anchor`.
 * `colour` controls the sprite and ring material colour.
 * The portal starts at scale=0 and expands to `targetScale`.
 */
function buildPortalSprite(
  anchor: { x: number; y: number; z: number },
  colour: number,
): {
  group: THREE.Group;
  material: THREE.SpriteMaterial;
  ring: THREE.Mesh;
  ringMat: THREE.MeshBasicMaterial;
} {
  const group = new THREE.Group();

  // Outer flat ring on ground plane
  const ringGeom = new THREE.RingGeometry(0.25, 0.6, 24);
  const ringMat = new THREE.MeshBasicMaterial({
    color: colour,
    transparent: true,
    opacity: 0.8,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(ringGeom, ringMat);
  ring.position.set(anchor.x, anchor.y, anchor.z);
  ring.rotation.x = Math.PI / 2;
  ring.scale.set(0, 0, 0);
  group.add(ring);

  // Inner glow sprite
  const spriteMat = new THREE.SpriteMaterial({
    color: colour,
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(spriteMat);
  sprite.position.set(anchor.x, anchor.y + 0.05, anchor.z);
  sprite.scale.set(0, 0, 1);
  group.add(sprite);

  return { group, material: spriteMat, ring, ringMat };
}

/**
 * Build the cast portal emitter.
 * Single crimson swirl at caster anchor, 300ms one-shot.
 */
function buildCastPortalEmitter(
  anchor: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  const { group: portalGroup, material, ringMat } = buildPortalSprite(anchor, CHALLENGER_COLOUR);
  group.add(portalGroup);

  const handle = registry.create('soul_duel_cast');
  state.handle = handle;
  const tl = handle.timeline;

  // Phase 1 (0 → CAST_LIFETIME/2): expand ring + sprite to full size
  const ring = portalGroup.children[0] as THREE.Mesh;
  const sprite = portalGroup.children[1] as THREE.Sprite;

  tl.to(
    ring.scale,
    { x: 1.2, y: 1.2, z: 1, duration: CAST_LIFETIME * 0.5, ease: 'back.out(1.7)' },
    0,
  );
  tl.to(
    sprite.scale,
    { x: 0.7, y: 0.7, z: 1, duration: CAST_LIFETIME * 0.5, ease: 'power2.out' },
    0,
  );

  // Phase 2 (CAST_LIFETIME/2 → CAST_LIFETIME): fade out
  const opProxy = { ring: 0.8, sprite: 0.9 };
  tl.to(
    opProxy,
    {
      ring: 0.0,
      sprite: 0.0,
      duration: CAST_LIFETIME * 0.5,
      ease: 'power1.in',
      onUpdate: () => {
        ringMat.opacity = opProxy.ring;
        material.opacity = opProxy.sprite;
      },
    },
    CAST_LIFETIME * 0.5,
  );

  // Mark finished
  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    CAST_LIFETIME,
  );

  return group;
}

/**
 * Build the active duel emitter.
 * Two portals (challenger crimson + defender blue) plus a duel-link beam.
 * All three live inside one Group. Long-lived (no built-in timeout).
 */
function buildActiveDuelEmitter(
  challengerAnchor: { x: number; y: number; z: number },
  defenderAnchor: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: DuelState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  // Challenger portal (crimson)
  const {
    group: challengerPortal,
    material: challengerMat,
    ring: cRing,
    ringMat: cRingMat,
  } = buildPortalSprite(challengerAnchor, CHALLENGER_COLOUR);
  group.add(challengerPortal);

  // Defender portal (blue)
  const {
    group: defenderPortal,
    material: defenderMat,
    ring: dRing,
    ringMat: dRingMat,
  } = buildPortalSprite(defenderAnchor, DEFENDER_COLOUR);
  group.add(defenderPortal);

  // Duel-link beam: a flat plane stretched between the two anchors.
  // Midpoint between the two anchors, length = distance, thin width.
  const midX = (challengerAnchor.x + defenderAnchor.x) / 2;
  const midY = (challengerAnchor.y + defenderAnchor.y) / 2;
  const midZ = (challengerAnchor.z + defenderAnchor.z) / 2;

  const dx = defenderAnchor.x - challengerAnchor.x;
  const dy = defenderAnchor.y - challengerAnchor.y;
  const dz = defenderAnchor.z - challengerAnchor.z;
  const beamLength = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1.0;
  const beamAngle = Math.atan2(dz, dx); // rotation in XZ plane

  const beamGeom = new THREE.PlaneGeometry(beamLength, 0.08);
  const beamMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.35,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const beam = new THREE.Mesh(beamGeom, beamMat);
  beam.position.set(midX, midY + 0.05, midZ);
  beam.rotation.x = Math.PI / 2; // lay flat
  beam.rotation.z = -beamAngle; // align with direction in XZ plane
  group.add(beam);

  const handle = registry.create('soul_duel_active');
  state.handle = handle;
  const tl = handle.timeline;

  // Portal expand (0 → 400ms): both portals expand to full size
  const cSpriteChild = challengerPortal.children[1] as THREE.Sprite;
  const dSpriteChild = defenderPortal.children[1] as THREE.Sprite;

  tl.to(cRing.scale, { x: 1.4, y: 1.4, z: 1, duration: 0.4, ease: 'back.out(1.7)' }, 0);
  tl.to(cSpriteChild.scale, { x: 0.9, y: 0.9, z: 1, duration: 0.4, ease: 'power2.out' }, 0);
  tl.to(dRing.scale, { x: 1.4, y: 1.4, z: 1, duration: 0.4, ease: 'back.out(1.7)' }, 0);
  tl.to(dSpriteChild.scale, { x: 0.9, y: 0.9, z: 1, duration: 0.4, ease: 'power2.out' }, 0);

  // Persistent pulse (400ms → ∞): both portals pulse in sync via shared proxy
  const pulseProxy = { value: 1.0 };
  tl.to(
    pulseProxy,
    {
      value: 0.5,
      duration: 0.8,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
      onUpdate: () => {
        cRingMat.opacity = pulseProxy.value * 0.8;
        challengerMat.opacity = pulseProxy.value * 0.9;
        dRingMat.opacity = pulseProxy.value * 0.8;
        defenderMat.opacity = pulseProxy.value * 0.9;
        beamMat.opacity = pulseProxy.value * 0.35;
      },
    },
    0.4,
  );

  return group;
}

/**
 * Build the resolution flare emitter.
 * Golden scale 0→2.0, opacity 1→0 over 500ms at winner position.
 */
function buildFlareEmitter(
  anchor: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  const flareMat = new THREE.SpriteMaterial({
    color: FLARE_COLOUR,
    transparent: true,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const flare = new THREE.Sprite(flareMat);
  flare.position.set(anchor.x, anchor.y, anchor.z);
  flare.scale.set(0, 0, 1);
  group.add(flare);

  // HF block-pulse fresh-timeline: use fresh handle for the new visual
  const handle = registry.create('soul_duel_flare');
  state.handle = handle;
  const tl = handle.timeline;

  // Scale 0 → 2.0 over full flare lifetime (ease out for pop effect)
  tl.to(flare.scale, { x: 2.0, y: 2.0, z: 1, duration: FLARE_LIFETIME, ease: 'power2.out' }, 0);

  // Opacity 1 → 0 over full flare lifetime
  const opProxy = { value: 1.0 };
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: FLARE_LIFETIME,
      ease: 'power1.in',
      onUpdate: () => {
        flareMat.opacity = opProxy.value;
      },
    },
    0,
  );

  // Mark finished at end
  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    FLARE_LIFETIME,
  );

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createSoulDuelPool
// ─────────────────────────────────────────────────────────────────────────────

export function createSoulDuelPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;

  /** phase='cast' — one-shot portals, max-active=2. */
  const castStates = new Map<THREE.Object3D, EmitterState>();

  /**
   * phase='active' — long-lived duel with TWO portals + beam.
   * Key = `${challengerId}|${defenderId}` (compound key via casterId overload).
   * Max active pairs = MAX_ACTIVE_DUELS.
   */
  const activeDuelMap = new Map<string, DuelState>();

  /** phase='impact' — resolution flare one-shots, max-active=4. */
  const flareStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    const { phase } = args;

    // ── Cast phase: caster anticipation portal ───────────────────────────
    if (phase === 'cast') {
      if (castStates.size >= MAX_CAST_ACTIVE) return null;
      const anchor = args.casterPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildCastPortalEmitter(anchor, deps, state);
      scene.add(group);
      castStates.set(group, state);
      return group;
    }

    // ── Active phase: two-portal duel (soul_duel_triggered) ──────────────
    // args.casterId = `${challengerId}|${defenderId}` (compound key).
    // args.casterPosition = challenger anchor.
    // args.targetPosition = defender anchor.
    if (phase === 'active') {
      const pairKey = args.casterId ?? '';
      if (!pairKey) return null;

      // F6 release-first: if same pair already active, release prior BEFORE acquiring new
      const prior = activeDuelMap.get(pairKey);
      if (prior) {
        prior.handle.dispose();
        scene.remove(prior.emitter);
        disposeSkillshotGroup(prior.emitter);
        activeDuelMap.delete(pairKey);
      }

      // Max active pairs guard (after release-first so re-trigger reuses slot)
      if (activeDuelMap.size >= MAX_ACTIVE_DUELS) return null;

      const challengerAnchor = args.casterPosition ?? { x: 0, y: 0, z: 0 };
      const defenderAnchor = args.targetPosition ?? { x: 1, y: 0, z: 0 };

      const state: DuelState = { handle: null!, emitter: null!, finished: false };
      const group = buildActiveDuelEmitter(challengerAnchor, defenderAnchor, deps, state);
      state.emitter = group;
      scene.add(group);
      activeDuelMap.set(pairKey, state);
      return group;
    }

    // ── Impact phase: resolve duel (soul_duel_resolved) ──────────────────
    // args.casterId = `${challengerId}|${defenderId}` (for active duel lookup).
    // args.targetPosition = winner position (flare anchor).
    if (phase === 'impact') {
      if (flareStates.size >= MAX_FLARE_ACTIVE) return null;

      // Release the active duel for this pair (portals + beam collapse)
      const pairKey = args.casterId ?? '';
      if (pairKey) {
        const duel = activeDuelMap.get(pairKey);
        if (duel) {
          duel.handle.dispose();
          scene.remove(duel.emitter);
          disposeSkillshotGroup(duel.emitter);
          activeDuelMap.delete(pairKey);
        }
      }

      // Winner flare: gold scale burst
      const flareAnchor = args.targetPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildFlareEmitter(flareAnchor, deps, state);
      scene.add(group);
      flareStates.set(group, state);
      return group;
    }

    return null;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    const all = new Set<THREE.Object3D>();
    for (const emitter of castStates.keys()) all.add(emitter);
    for (const { emitter } of activeDuelMap.values()) all.add(emitter);
    for (const emitter of flareStates.keys()) all.add(emitter);
    return all;
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const castState = castStates.get(emitter);
    if (castState) return castState.finished;

    for (const state of activeDuelMap.values()) {
      if (state.emitter === emitter) return state.finished;
    }

    const flareState = flareStates.get(emitter);
    if (flareState) return flareState.finished;

    // Unknown emitter
    return true;
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    // Check cast portal pool
    const castState = castStates.get(emitter);
    if (castState) {
      castState.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      castStates.delete(emitter);
      return;
    }

    // Check active duel map (by emitter reference)
    for (const [pairKey, state] of activeDuelMap.entries()) {
      if (state.emitter === emitter) {
        state.handle.dispose();
        scene.remove(emitter);
        disposeSkillshotGroup(emitter);
        activeDuelMap.delete(pairKey);
        return;
      }
    }

    // Check resolution flare pool
    const flareState = flareStates.get(emitter);
    if (flareState) {
      flareState.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      flareStates.delete(emitter);
      return;
    }
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────
  // Iterates ALL three active sets/maps. Idempotent via closure `disposed` flag.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // 1. Cast portal active set
    for (const [emitter, state] of castStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    castStates.clear();

    // 2. Active duel Map<pairKey, DuelState>
    for (const state of activeDuelMap.values()) {
      state.handle.dispose();
      scene.remove(state.emitter);
      disposeSkillshotGroup(state.emitter);
    }
    activeDuelMap.clear();

    // 3. Resolution flare active set
    for (const [emitter, state] of flareStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    flareStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveSoulDuelForEvent
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolver for Soul Duel VFX events.
 *
 * Compound key encoding convention:
 *   - `item_cast` (phase='cast'):
 *       args.casterId = challengerId (caster's playerId).
 *       args.casterPosition = caster world position.
 *
 *   - `soul_duel_triggered` (phase='active'):
 *       args.casterId = `${challengerId}|${defenderId}` — compound pair key.
 *       args.casterPosition = challenger world position.
 *       args.targetPosition = defender world position.
 *
 *   - `soul_duel_resolved` (phase='impact'):
 *       args.casterId = `${winnerId}|${loserId}` — used by pool to look up
 *       and release the active duel (portals+beam). NOTE: the pool must also
 *       handle the case where the triggered event encoded the pair as
 *       `${challengerId}|${defenderId}` (which is `${winnerId}|${loserId}`
 *       when the winner was the challenger, or `${loserId}|${winnerId}` when
 *       not). The manager is responsible for passing consistent ordering;
 *       see notes below.
 *       args.targetPosition = winner world position (golden flare anchor).
 *
 * IMPORTANT: The SoulDuelResolvedEvent carries winnerId + loserId, NOT
 * challengerId + defenderId. The resolver encodes the compound key as
 * `${winnerId}|${loserId}`. The game server must ensure this ordering is
 * consistent with how the SoulDuelTriggeredEvent was encoded (challengerId
 * first). If the challengers win, the keys match; if the defender wins, they
 * are reversed. The pool's impact handler uses the casterId to look up and
 * release the active duel — if the key ordering differs, the duel won't be
 * found and released. This is a known limitation: it is resolved at the
 * manager level (Task 6.1) or by passing challengerId+defenderId directly
 * on the resolved event. For now, the resolver encodes the key consistently
 * as provided by the event.
 *
 * @param event               - The game event to resolve.
 * @param getPositionForPlayer - Optional: playerId → world position.
 */
export function resolveSoulDuelForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'soul_duel'; args: ItemVfxSpawnArgs } | null {
  // ── item_cast with itemId='soul_duel' → cast portal ──────────────────────
  if (event.type === 'item_cast') {
    const cast = event as ItemCastEvent;
    if (cast.itemId !== 'soul_duel') return null;
    return {
      poolId: 'soul_duel',
      args: {
        phase: 'cast',
        casterId: cast.casterId,
        casterPosition: getPositionForPlayer?.(cast.casterId),
      },
    };
  }

  // ── soul_duel_triggered → TWO portals + duel-link beam (active phase) ────
  if (event.type === 'soul_duel_triggered') {
    const triggered = event as SoulDuelTriggeredEvent;
    const { challengerId, defenderId } = triggered;
    const pairKey = `${challengerId}|${defenderId}`;
    return {
      poolId: 'soul_duel',
      args: {
        phase: 'active',
        // casterId carries the compound pair key
        casterId: pairKey,
        // casterPosition = challenger anchor
        casterPosition: getPositionForPlayer?.(challengerId),
        // targetPosition = defender anchor
        targetPosition: getPositionForPlayer?.(defenderId),
      },
    };
  }

  // ── soul_duel_resolved → portals collapse + winner golden flare ───────────
  if (event.type === 'soul_duel_resolved') {
    const resolved = event as SoulDuelResolvedEvent;
    const { winnerId, loserId } = resolved;
    const pairKey = `${winnerId}|${loserId}`;
    return {
      poolId: 'soul_duel',
      args: {
        phase: 'impact',
        // casterId carries the compound key for active duel lookup/release
        casterId: pairKey,
        // targetPosition = winner world position (golden flare anchor)
        targetPosition: getPositionForPlayer?.(winnerId),
      },
    };
  }

  return null;
}
